import { Button, Icon } from '@iwoca/orion';
import { Trans } from '@lingui/macro';
import clsx from 'clsx';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import guarantorStyles from '../Guarantors/Guarantors.module.css';
import { TSignupFormDataDE } from '../NewSignupFormDE/NewSignupFormDE.types';
import { PersonalDetailsDE } from '../PersonalDetailsDE/PersonalDetailsDE';
import styles from './GuarantorsDE.module.css';

export const GuarantorsDE = () => {
  const { trackEvent } = useAmplitude();
  const { setValue, watch } = useFormContext<TSignupFormDataDE>();

  const watchPrivacyPolicy = watch('applicant.privacyPolicyAgreement');

  const { fields, append, remove } = useFieldArray({
    name: 'guarantor',
  });

  const setPrivacyPolicy = (index: number) => {
    setValue(`guarantor.${index}.privacyPolicyAgreement`, watchPrivacyPolicy);
  };

  const handleRemoveGuarantor = (index: number) => {
    remove(index);
    trackEvent(AMPLITUDE_EVENTS.extraDetailsRemoved, {
      'Details removed': 'Guarantor',
      'CTA text': '',
    });
  };

  const handleAddGuarantor = () => {
    append({});
    trackEvent(AMPLITUDE_EVENTS.extraDetailsAdded, {
      'Details added': 'New contact',
      'CTA text': 'Add new contact',
    });
  };

  const { checkIntroducerPermission } = useIntroducerContext();
  const isDisabled = !checkIntroducerPermission('can_create_applications');

  return (
    <div
      className={clsx(guarantorStyles.container, {
        [styles.buttonContainer]: fields.length === 0,
      })}
    >
      {fields.map((field, index) => {
        setPrivacyPolicy(index);

        return (
          <section key={field.id} className={clsx(styles.fieldContainer)}>
            <hr className={styles.seperator} />
            <div className={styles.header}>
              <h2>
                <Trans>Contact</Trans> {index + 1}
              </h2>
              <Button
                type="button"
                data-testid="remove-guarantor-button"
                variant="tertiary"
                className={clsx(
                  guarantorStyles.guarantorButton,
                  guarantorStyles.removeGuarantor,
                )}
                onClick={() => handleRemoveGuarantor(index)}
              >
                <Icon icon="trashFilled" />
              </Button>
            </div>
            <PersonalDetailsDE roleType="guarantor" index={index} />
          </section>
        );
      })}
      <ButtonLink
        type="button"
        className={clsx(styles.addGuarantorButton, {
          [styles.addAnotherGuarantor]: fields.length > 0,
        })}
        onClick={handleAddGuarantor}
        disabled={isDisabled}
        aria-disabled={isDisabled}
      >
        <Icon className={styles.addIcon} icon="plus" />
        <Trans>Add new contact</Trans>
      </ButtonLink>
    </div>
  );
};
